<template>
  <div class="isolate mm-root t24-tools mm-clips clip-tasks-page">
    <section ref="taskListRef" class="clip-tasks-shell">
      <header class="clip-tasks-header">
        <div>
          <NuxtLink class="clip-tasks-back" to="/clips">
            <span class="i-lucide-arrow-left" aria-hidden="true"></span>
            {{ t('clips.tasks.backToCreate') }}
          </NuxtLink>
          <h1>{{ t('clips.tasks.title') }}</h1>
          <p>{{ t('clips.tasks.subtitle') }}</p>
        </div>
        <button class="clip-tasks-secondary clip-tasks-create" type="button" @click="goCreate">
          <span class="i-lucide-wand-sparkles" aria-hidden="true"></span>
          {{ t('clips.actions.createPackage') }}
        </button>
      </header>

      <form class="clip-tasks-toolbar" @submit.prevent="refreshClipTasks">
        <div class="clip-tasks-filters"><MmSegmented :model-value="activeFilter" :label="t('clips.tasks.filters.label')" :options="taskFilters.map(filter => ({ value: filter.value, label: taskFilterLabel(filter) }))" @update:model-value="selectTaskFilter($event as ClipPackageTaskFilter)" /></div>
        <label class="clip-tasks-filter-select">
          <span class="field-label">{{ t('clips.tasks.filters.label') }}</span>
          <MmSelect :model-value="activeFilter" :label="t('clips.tasks.filters.label')" :options="taskFilters.map(filter => ({ value: filter.value, label: taskFilterLabel(filter) }))" @change="selectTaskFilter($event as ClipPackageTaskFilter)" />
        </label>
        <label class="clip-tasks-search">
          <span class="i-lucide-search" aria-hidden="true"></span>
          <input
            v-model="searchQuery"
            name="q"
            type="search"
            :aria-label="t('clips.tasks.searchPlaceholder')"
            :placeholder="t('clips.tasks.searchPlaceholder')"
          />
        </label>
        <button class="clip-tasks-ghost" type="submit" :disabled="isLoading">
          <span class="i-lucide-refresh-cw" aria-hidden="true"></span>
          {{ t(searchQuery.trim() !== routeTaskQuery() ? 'common.search' : 'clips.tasks.refresh') }}
        </button>
        <MmSegmented v-model="taskView" class="clip-tasks-view" :label="t('mm.library.view')" :options="[{ value: 'list', label: t('mm.library.list') }, { value: 'grid', label: t('mm.library.grid') }]" />
      </form>

      <div v-if="errorMessage && clipTasks.length" class="clip-tasks-error" role="alert">
        <p>{{ errorMessage }}</p>
        <button class="clip-tasks-ghost" type="button" :disabled="isLoading" @click="refreshClipTasks">{{ t('ux.retry') }}</button>
      </div>
      <p v-if="batchContextUnavailable && clipTasks.length" class="clip-tasks-context-notice" role="status">{{ t('clips.tasks.batchContextUnavailable') }}</p>
      <p v-if="isLoading && clipTasks.length && !isLoadingMore" role="status" class="text-sm text-[var(--text-2)]">{{ t('common.loading') }}</p>

      <section v-if="isLoading && !clipTasks.length" class="clip-tasks-empty" aria-live="polite">
        <span class="i-lucide-loader-circle clip-tasks-spin" aria-hidden="true"></span>
        <h2>{{ t('clips.tasks.loading') }}</h2>
      </section>

      <section v-else-if="clipTasks.length" class="clip-tasks-list" :class="{ 'is-grid': taskView === 'grid' }" aria-live="polite">
        <article v-for="item in clipTasks" :key="item.package_id" class="clip-task-card" :data-package-id="item.package_id" tabindex="-1">
          <div class="clip-task-main">
            <div class="clip-task-title-row">
              <span class="clip-task-status" :class="`status-${item.status}`">
                {{ taskStatusLabel(item) }}
              </span>
              <time>{{ formatClipTaskDate(item.updated_at) }}</time>
            </div>
            <h2>{{ item.title }}</h2>
            <p v-if="item.platform || item.duration_seconds" class="clip-task-context">{{ [item.platform, item.use_case, item.duration_seconds ? `${item.duration_seconds}s` : ''].filter(Boolean).join(' · ') }}</p>
            <p v-if="item.batch_id" class="clip-task-batch-context">{{ t('clips.tasks.batchDirection', { direction: clipTaskBatchDirection(item) }) }}<span v-if="item.batch_created"> · {{ t('clips.tasks.batchDate', { date: formatClipTaskDate(item.batch_created) }) }}</span></p>
            <p>{{ item.brief_summary || t('clips.tasks.empty.brief') }}</p>
            <MmDisclosure class="clip-task-details" variant="divider" nested :label="t('clips.tasks.details')">
              <div class="clip-task-flags" :aria-label="t('clips.tasks.details')">
                <span :class="{ active: item.has_music_task }"><span :class="item.has_music_task ? 'i-lucide-check' : 'i-lucide-minus'" aria-hidden="true"></span>{{ t('clips.tasks.flags.music') }}<span class="sr-only"> · {{ t(item.has_music_task ? 'clips.tasks.available' : 'clips.tasks.notYet') }}</span></span>
                <span :class="{ active: item.has_candidates }"><span :class="item.has_candidates ? 'i-lucide-check' : 'i-lucide-minus'" aria-hidden="true"></span>{{ t('clips.tasks.flags.candidates') }}<span class="sr-only"> · {{ t(item.has_candidates ? 'clips.tasks.available' : 'clips.tasks.notYet') }}</span></span>
                <span :class="{ active: item.has_selected_track }"><span :class="item.has_selected_track ? 'i-lucide-check' : 'i-lucide-minus'" aria-hidden="true"></span>{{ t('clips.tasks.flags.selected') }}<span class="sr-only"> · {{ t(item.has_selected_track ? 'clips.tasks.available' : 'clips.tasks.notYet') }}</span></span>
                <span :class="{ active: item.has_license }"><span :class="item.has_license ? 'i-lucide-check' : 'i-lucide-minus'" aria-hidden="true"></span>{{ t('clips.tasks.flags.license') }}<span class="sr-only"> · {{ t(item.has_license ? 'clips.tasks.available' : 'clips.tasks.notYet') }}</span></span>
                <span :class="{ active: item.is_public }"><span :class="item.is_public ? 'i-lucide-check' : 'i-lucide-minus'" aria-hidden="true"></span>{{ t('clips.tasks.flags.public') }}<span class="sr-only"> · {{ t(item.is_public ? 'clips.tasks.available' : 'clips.tasks.notYet') }}</span></span>
              </div>
              <div class="clip-task-flags">
                <span :class="{ active: Number(item.visual_asset_count || 0) > 0 }"><span :class="Number(item.visual_asset_count || 0) > 0 ? 'i-lucide-check' : 'i-lucide-minus'" aria-hidden="true"></span>{{ clipTaskVisualLabel(item) }}<span class="sr-only"> · {{ t(Number(item.visual_asset_count || 0) > 0 ? 'clips.tasks.available' : 'clips.tasks.notYet') }}</span></span>
                <span :class="{ active: item.mv_reference_ready }"><span :class="item.mv_reference_ready ? 'i-lucide-check' : 'i-lucide-minus'" aria-hidden="true"></span>{{ clipTaskReferenceLabel(item) }}<span class="sr-only"> · {{ t(item.mv_reference_ready ? 'clips.tasks.available' : 'clips.tasks.notYet') }}</span></span>
              </div>
            </MmDisclosure>
            <p v-if="item.failure_reason" class="clip-task-failure">{{ item.failure_reason }}</p>
            <div v-if="item.highlight_status" class="clip-task-highlight" :class="`highlight-${item.highlight_status}`">
              <span :class="clipTaskHighlightIcon(item)" aria-hidden="true"></span>
              <span>{{ clipTaskHighlightLabel(item) }}</span>
              <button
                v-if="item.highlight_status === 'failed'"
                type="button"
                :disabled="!!retryingHighlightPackageId"
                @click.stop="retryClipTaskHighlight(item)"
              >
                {{ t('clips.tasks.highlight.retry') }}
              </button>
            </div>
            <p v-if="highlightRetryError?.packageId === item.package_id" class="clip-task-failure" role="alert">{{ highlightRetryError.message }}</p>
          </div>
          <div class="clip-task-actions">
            <button class="clip-tasks-secondary" type="button" @click="resumeClipPackage(item)">
              <span class="i-lucide-play" aria-hidden="true"></span>
              {{ taskActionLabel(item) }}
            </button>
            <ClipsTaskMenu :title="item.title" :resume-label="taskActionLabel(item)" :is-public="item.is_public" @resume="resumeClipPackage(item)" @share="openClipShare(item)" />
          </div>
        </article>
        <p v-if="loadMoreError" class="clip-tasks-error" role="alert">{{ loadMoreError }}</p>
        <button v-if="nextCursor" class="clip-tasks-ghost clip-tasks-more" type="button" :disabled="isLoading" @click="loadMoreClipTasks">
          <span class="i-lucide-list-plus" aria-hidden="true"></span>
          {{ t(isLoadingMore ? 'common.loading' : loadMoreError ? 'ux.retry' : 'clips.tasks.loadMore') }}
        </button>
      </section>

      <section v-else-if="errorMessage" class="clip-tasks-empty" role="alert">
        <MmStateArt kind="error" /><h2>{{ t('clips.tasks.errors.loadFailed') }}</h2>
        <p>{{ errorMessage }}</p>
        <button type="button" class="clip-tasks-secondary" @click="refreshClipTasks">{{ t('ux.retry') }}</button>
      </section>

      <section v-else class="clip-tasks-empty">
        <MmStateArt v-if="!hasTaskFilters" kind="empty" /><span v-else class="i-lucide-search" aria-hidden="true"></span>
        <h2>{{ t(hasTaskFilters ? 'clips.tasks.empty.filteredTitle' : 'clips.tasks.empty.title') }}</h2>
        <p>{{ t(hasTaskFilters ? 'clips.tasks.empty.filteredSubtitle' : 'clips.tasks.empty.subtitle') }}</p>
        <div v-if="!hasTaskFilters" class="clip-task-demos">
          <article v-for="demo in clipTaskDemoCards" :key="demo.titleKey">
            <strong>{{ t(demo.titleKey) }}</strong>
            <span>{{ t(demo.bodyKey) }}</span>
          </article>
        </div>
        <button class="clip-tasks-primary" type="button" @click="goCreate">
          <span class="i-lucide-plus" aria-hidden="true"></span>
          {{ t('clips.actions.createPackage') }}
        </button>
      </section>
    </section>
  </div>
</template>

<script setup lang="ts">
import MmStateArt from '~/components/mm/base/MmStateArt.vue'
import MmDisclosure from '~/components/mm/base/MmDisclosure.vue';

import '~/components/mm/clips/clips.css';
import MmSegmented from '~/components/mm/base/MmSegmented.vue';
import MmSelect from '~/components/mm/MmSelect.vue';
import ClipsTaskMenu from '~/components/mm/clips/ClipsTaskMenu.vue';
import { computed, nextTick, onActivated, onDeactivated, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSession } from '#imports';
import musicService, {
  type ClipPackage,
  type ClipPackageListResponse,
  type ClipPackageTaskFilter,
  type ClipPackageTaskListItem,
} from '~/services/musicService';
import {
  createGrowthEventId,
  getGrowthEventAnonymousId,
  getGrowthEventSessionId,
} from '~/utils/growthEvents';
import { buildClipDraftLocation } from '~/utils/clip-workspace.mjs';

const taskView = ref('list');
// Reuse the Library presentation preference; task filters and URL state stay independent.
onMounted(() => { try { const saved = localStorage.getItem('musicmint_library_view'); if (saved === 'grid' || saved === 'list') taskView.value = saved; } catch {} });
watch(taskView, value => { try { localStorage.setItem('musicmint_library_view', value); } catch {} });
const router = useRouter();
const route = useRoute();
const nuxtApp = useNuxtApp();
const { t } = useI18n();
const { isAuthenticated, isAuthReady, user, handleSignIn, fetchAndStoreToken } = useAuthState();
const { session } = useSession();

const taskFilters: Array<{ value: ClipPackageTaskFilter; labelKey: string }> = [
  { value: 'priority', labelKey: 'clips.tasks.filters.priority' },
  { value: 'all', labelKey: 'clips.tasks.filters.all' },
  { value: 'in_progress', labelKey: 'clips.tasks.filters.inProgress' },
  { value: 'needs_action', labelKey: 'clips.tasks.filters.needsAction' },
  { value: 'failed', labelKey: 'clips.tasks.filters.failed' },
  { value: 'published', labelKey: 'clips.tasks.filters.published' },
];

const routeTaskFilter = () => taskFilters.find(filter => filter.value === route.query.status)?.value || 'priority';
const routeTaskQuery = () => typeof route.query.q === 'string' ? route.query.q.trim() : '';
const activeFilter = ref<ClipPackageTaskFilter>(routeTaskFilter());
const searchQuery = ref(routeTaskQuery());
const hasTaskFilters = computed(() => activeFilter.value !== 'all' || Boolean(routeTaskQuery()));
const clipTasks = ref<ClipPackageTaskListItem[]>([]);
const nextCursor = ref<number | null>(null);
const nextCursorCreated = ref<number | undefined>();
const nextCursorId = ref<string | undefined>();
const isLoading = ref(!isAuthReady.value);
const isLoadingMore = ref(false);
const loadMoreError = ref('');
const errorMessage = ref('');
const batchContextUnavailable = ref(false);
const retryingHighlightPackageId = ref('');
const highlightRetryError = ref<{ packageId: string; message: string } | null>(null);
const taskListRef = ref<HTMLElement | null>(null);
const lastOpenedPackageId = ref('');
let listOwner = '';
let listRequest = 0;
let highlightRequest = 0;
let releasePaginationFocus = () => {};
let mounted = false;
const taskSessionKey = () => JSON.stringify([isAuthReady.value, isAuthenticated.value, user.value?.id || '', session.value?.id || '']);
const isCurrentTaskRequest = (request: number, owner: string, filter: ClipPackageTaskFilter, query: string) => (
  mounted && request === listRequest && owner === taskSessionKey() && filter === routeTaskFilter() && query === routeTaskQuery()
);
useHead(() => ({ title: `${t('clips.tasks.title')} · MusicMint` }));
const clipTaskDemoCards = [
  { titleKey: 'clips.tasks.empty.demo1.title', bodyKey: 'clips.tasks.empty.demo1.body' },
  { titleKey: 'clips.tasks.empty.demo2.title', bodyKey: 'clips.tasks.empty.demo2.body' },
  { titleKey: 'clips.tasks.empty.demo3.title', bodyKey: 'clips.tasks.empty.demo3.body' },
];

const assertSuccessfulApiPayload = <T,>(payload: any): T => {
  if (payload?.code === 401) {
    throw new Error(String(t('clips.errors.loginRequired')));
  }
  if (typeof payload?.code === 'number' && payload?.code >= 400) {
    throw new Error(payload?.msg || String(t('clips.tasks.errors.loadFailed')));
  }
  return (payload?.data ?? payload) as T;
};

const captureClipPaginationFocus = (trigger: HTMLButtonElement | null | undefined) => {
  releasePaginationFocus();
  let moved = !trigger || document.activeElement !== trigger;
  const attached = !moved;
  const onFocus = (event: FocusEvent) => { if (event.target !== document.body && event.target !== trigger) moved = true; };
  const onPointer = (event: Event) => { if (!trigger?.contains(event.target as Node)) moved = true; };
  if (attached) {
    document.addEventListener('focusin', onFocus);
    document.addEventListener('pointerdown', onPointer);
  }
  const release = () => {
    moved = true;
    if (!attached) return;
    document.removeEventListener('focusin', onFocus);
    document.removeEventListener('pointerdown', onPointer);
  };
  releasePaginationFocus = release;
  return { owns: () => !moved, release };
};

const restoreClipPaginationFocus = async (request: number, owner: string, filter: ClipPackageTaskFilter, query: string, trigger: HTMLButtonElement, successorId = '', focusOwned = () => true) => {
  await nextTick();
  if (!isCurrentTaskRequest(request, owner, filter, query) || !focusOwned()) return;
  if (document.activeElement !== trigger && document.activeElement !== document.body) return;
  const target = successorId
    ? Array.from(taskListRef.value?.querySelectorAll<HTMLElement>('[data-package-id]') || []).find(item => item.dataset.packageId === successorId)
    : trigger;
  if (!target?.isConnected) return;
  target.focus({ preventScroll: true });
  target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
};

const loadClipTasks = async (append = false) => {
  if (!mounted || !isAuthReady.value) return;
  if (append && (isLoading.value || !nextCursor.value)) return;
  if (!isAuthenticated.value) {
    errorMessage.value = String(t('clips.errors.loginRequired'));
    await handleSignIn();
    return;
  }
  const trigger = append ? taskListRef.value?.querySelector<HTMLButtonElement>('.clip-tasks-more') : null;
  const paginationFocus = captureClipPaginationFocus(trigger);
  const previousIds = new Set(clipTasks.value.map(item => item.package_id));
  let successorId = '';
  errorMessage.value = '';
  loadMoreError.value = '';
  isLoading.value = true;
  isLoadingMore.value = append;
  const request = ++listRequest;
  const owner = taskSessionKey();
  const filter = activeFilter.value;
  const query = routeTaskQuery();
  const params = {
    status: filter,
    q: query,
    cursor: append ? nextCursor.value || undefined : undefined,
    cursor_created: append ? nextCursorCreated.value : undefined,
    cursor_id: append ? nextCursorId.value : undefined,
    limit: 20,
  };
  try {
    const token = await fetchAndStoreToken();
    if (!isCurrentTaskRequest(request, owner, filter, query)) return;
    if (!token) throw new Error(String(t('user.sessionExpired')));
    const response = await musicService.listClipPackages(params, { musicmintInlineError: true });
    if (!isCurrentTaskRequest(request, owner, filter, query)) return;
    const data = assertSuccessfulApiPayload<ClipPackageListResponse['data']>(response);
    if (!Array.isArray(data?.items) || data.items.some(item => !item?.package_id)) throw new Error(String(t('clips.tasks.errors.loadFailed')));
    clipTasks.value = append ? [...new Map([...clipTasks.value, ...data.items].map(item => [item.package_id, item])).values()] : data.items;
    if (append) successorId = data.items.find(item => !previousIds.has(item.package_id))?.package_id || (!data.next_cursor ? clipTasks.value.at(-1)?.package_id || '' : '');
    batchContextUnavailable.value = append ? batchContextUnavailable.value || data.batch_context_available === false : data.batch_context_available === false;
    nextCursor.value = data.next_cursor || null;
    nextCursorCreated.value = data.next_cursor_created || undefined;
    nextCursorId.value = data.next_cursor_id || undefined;
    listOwner = owner;
    trackClipTaskEvent('clips_task_list_view', {
      filter,
      count: clipTasks.value.length,
      has_query: Boolean(query),
    });
    if (!clipTasks.value.length) {
      trackClipTaskEvent('clips_task_list_empty_view', { filter });
    }
  } catch (error: any) {
    if (isCurrentTaskRequest(request, owner, filter, query)) {
      if (append) loadMoreError.value = String(t('clips.tasks.errors.loadMoreFailed'));
      else errorMessage.value = error?.message || String(t('clips.tasks.errors.loadFailed'));
    }
  } finally {
    try {
      if (isCurrentTaskRequest(request, owner, filter, query)) {
        isLoading.value = false;
        isLoadingMore.value = false;
        if (trigger && paginationFocus.owns()) await restoreClipPaginationFocus(request, owner, filter, query, trigger, successorId, paginationFocus.owns);
      }
    } finally { paginationFocus.release(); }
  }
};

const refreshClipTasks = () => {
  trackClipTaskEvent('clips_task_refresh_click', {
    filter: activeFilter.value,
    has_query: Boolean(searchQuery.value.trim()),
  });
  const q = searchQuery.value.trim();
  if (q !== routeTaskQuery()) void router.replace({ query: { ...route.query, q: q || undefined } });
  else void loadClipTasks(false);
};

const loadMoreClipTasks = () => {
  if (isLoading.value) return;
  void loadClipTasks(true);
};

const selectTaskFilter = (filter: ClipPackageTaskFilter) => {
  if (activeFilter.value === filter) return;
  trackClipTaskEvent('clips_task_filter_change', { filter });
  void router.replace({ query: { ...route.query, status: filter === 'priority' ? undefined : filter, q: searchQuery.value.trim() || undefined } });
};

watch([() => route.query.status, () => route.query.q], () => {
  releasePaginationFocus();
  ++listRequest;
  ++highlightRequest;
  retryingHighlightPackageId.value = '';
  highlightRetryError.value = null;
  activeFilter.value = routeTaskFilter();
  searchQuery.value = routeTaskQuery();
  nextCursor.value = null;
  nextCursorCreated.value = undefined;
  nextCursorId.value = undefined;
  loadMoreError.value = '';
  clipTasks.value = [];
  batchContextUnavailable.value = false;
  if (mounted && isAuthReady.value) void loadClipTasks(false);
});

watch([isAuthReady, isAuthenticated, () => user.value?.id, () => session.value?.id], ([ready, authenticated]) => {
  releasePaginationFocus();
  ++listRequest;
  ++highlightRequest;
  retryingHighlightPackageId.value = '';
  highlightRetryError.value = null;
  lastOpenedPackageId.value = '';
  clipTasks.value = [];
  batchContextUnavailable.value = false;
  nextCursor.value = null;
  nextCursorCreated.value = undefined;
  nextCursorId.value = undefined;
  loadMoreError.value = '';
  errorMessage.value = '';
  listOwner = '';
  if (!mounted) return;
  if (!ready) { isLoading.value = true; return; }
  if (authenticated) void loadClipTasks(false);
  else { isLoading.value = false; errorMessage.value = String(t('clips.errors.loginRequired')); }
});

const taskFilterLabel = (filter: { value: ClipPackageTaskFilter; labelKey: string }) => {
  if (filter.value === 'priority') return t('clips.tasks.filters.priority');
  if (filter.value === 'needs_action') return t('clips.tasks.filters.needsAction');
  return t(filter.labelKey);
};

const resumeClipPackage = (item: ClipPackageTaskListItem) => {
  lastOpenedPackageId.value = item.package_id;
  trackClipTaskEvent('clips_task_resume_click', {
    package_id: item.package_id,
    status: item.status,
    next_action: item.next_action,
  });
  if (item.next_action === 'retry') {
    trackClipTaskEvent('clips_task_retry_click', {
      package_id: item.package_id,
      status: item.status,
    });
  }
  if (item.next_action === 'view_share' && item.is_public) {
    router.push(`/clips/${item.package_id}`);
    return;
  }
  router.push(`/clips?package_id=${encodeURIComponent(item.package_id)}${item.batch_id ? `&batch_id=${encodeURIComponent(item.batch_id)}` : ''}`);
};

const clipTaskBatchDirection = (item: ClipPackageTaskListItem) => {
  const key = `clips.directions.${item.batch_direction_id}.title`;
  const translated = t(key);
  return translated === key ? item.batch_direction_title || item.batch_direction_id || t('clips.batch.variantFallback') : translated;
};

const openClipShare = (item: ClipPackageTaskListItem) => {
  lastOpenedPackageId.value = item.package_id;
  router.push(`/clips/${item.package_id}`);
};

const goCreate = () => {
  router.push(buildClipDraftLocation('new', createGrowthEventId()));
};

const taskStatusLabel = (item: ClipPackageTaskListItem) => {
  const translated = t(`clips.tasks.status.${item.status}`);
  return translated === `clips.tasks.status.${item.status}` ? item.status_label || item.status : translated;
};

const taskActionLabel = (item: ClipPackageTaskListItem) => {
  const translated = t(`clips.tasks.actions.${item.next_action}`);
  return translated === `clips.tasks.actions.${item.next_action}` ? item.next_action : translated;
};

const clipTaskVisualLabel = (item: ClipPackageTaskListItem) => (
  t('clips.tasks.visuals.count', { count: Number(item.visual_asset_count || 0) })
);

const clipTaskReferenceLabel = (item: ClipPackageTaskListItem) => {
  if (!item.mv_reference_source) return t('clips.tasks.references.missing');
  const translated = t(`clips.tasks.references.${item.mv_reference_source}`);
  return translated === `clips.tasks.references.${item.mv_reference_source}` ? item.mv_reference_source : translated;
};

const formatHighlightSecond = (value?: number) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? (Math.round(numeric * 10) / 10).toFixed(1) : '';
};

const clipTaskHighlightLabel = (item: ClipPackageTaskListItem) => {
  if (item.highlight_status === 'succeeded' && Number.isFinite(Number(item.highlight_start)) && Number.isFinite(Number(item.highlight_end))) {
    return t('clips.tasks.highlight.succeeded', {
      start: formatHighlightSecond(item.highlight_start),
      end: formatHighlightSecond(item.highlight_end),
    });
  }
  if (item.highlight_status === 'failed') return t('clips.tasks.highlight.failed');
  if (item.highlight_status === 'pending') return t('clips.tasks.highlight.pending');
  if (item.highlight_status === 'processing') return t('clips.tasks.highlight.processing');
  return '';
};

const clipTaskHighlightIcon = (item: ClipPackageTaskListItem) => {
  if (item.highlight_status === 'succeeded') return 'i-lucide-check-circle-2';
  if (item.highlight_status === 'failed') return 'i-lucide-alert-triangle';
  if (item.highlight_status === 'processing') return 'i-lucide-loader-circle clip-tasks-spin';
  return 'i-lucide-clock-3';
};

const retryClipTaskHighlight = async (item: ClipPackageTaskListItem) => {
  if (!mounted || !isAuthReady.value || retryingHighlightPackageId.value) return;
  if (!isAuthenticated.value) {
    errorMessage.value = String(t('clips.errors.loginRequired'));
    await handleSignIn();
    return;
  }

  retryingHighlightPackageId.value = item.package_id;
  highlightRetryError.value = null;
  const request = ++highlightRequest;
  const owner = taskSessionKey();
  const ownsAction = () => mounted && request === highlightRequest && owner === taskSessionKey();
  const current = () => ownsAction() && clipTasks.value.includes(item);
  trackClipTaskEvent('clips_task_highlight_retry_click', {
    package_id: item.package_id,
    highlight_status: item.highlight_status || '',
  });

  try {
    const token = await fetchAndStoreToken();
    if (!current()) return;
    if (!token) throw new Error(String(t('user.sessionExpired')));
    const detailResponse = await musicService.getClipPackage({ package_id: item.package_id }, { musicmintInlineError: true });
    if (!current()) return;
    const detail = assertSuccessfulApiPayload<ClipPackage>(detailResponse);
    const candidates = detail.candidates || [];
    const candidate = candidates.find((entry) => entry.highlight_status === 'failed') || candidates[0];
    if (!detail.music_task_id || !candidate?.music_id) {
      throw new Error(String(t('clips.errors.highlightRetryMissing')));
    }

    const response = await musicService.analyzeMusicHighlight({
      package_id: detail.id,
      music_task_id: detail.music_task_id,
      music_id: candidate.music_id,
      requested_clip_seconds: detail.brief.duration_seconds || candidate.duration_seconds,
      force: true,
    }, { musicmintInlineError: true });
    if (!current()) return;
    assertSuccessfulApiPayload(response);
    item.highlight_status = 'pending';
    item.highlight_error = '';
    await loadClipTasks(false);
  } catch (error: any) {
    if (current()) highlightRetryError.value = { packageId: item.package_id, message: error?.message || String(t('clips.errors.highlightRetryFailed')) };
  } finally {
    if (ownsAction()) retryingHighlightPackageId.value = '';
  }
};

const formatClipTaskDate = (value: number) => {
  if (!value) return '';
  return new Date(value).toLocaleString();
};

const trackClipTaskEvent = (eventType: string, metadata: Record<string, unknown> = {}) => {
  void musicService.trackGrowthEvent({
    event_id: createGrowthEventId(),
    content_type: 'clip_package',
    content_id: typeof metadata.package_id === 'string' ? metadata.package_id : 'clips-tasks',
    event_type: eventType,
    surface: 'clips',
    request_id: createGrowthEventId(),
    anonymous_id: getGrowthEventAnonymousId(),
    session_id: getGrowthEventSessionId(),
    metadata,
  }).catch(() => {});
};

onMounted(() => {
  mounted = true;
  if (isAuthReady.value) void loadClipTasks(false);
});
onActivated(() => {
  if (mounted) return;
  mounted = true;
  if (!isAuthReady.value || listOwner !== taskSessionKey()) {
    clipTasks.value = [];
    batchContextUnavailable.value = false;
    nextCursor.value = null;
    nextCursorCreated.value = undefined;
    nextCursorId.value = undefined;
    loadMoreError.value = '';
  }
  if (isAuthReady.value && !clipTasks.value.length) { void loadClipTasks(false); return; }
});
onDeactivated(() => { releasePaginationFocus(); mounted = false; ++listRequest; ++highlightRequest; retryingHighlightPackageId.value = ''; isLoading.value = false; isLoadingMore.value = false; });

const restoreClipTaskPosition = async () => {
  await nextTick();
  // Nuxt restores its outer-page scroll on the first frame after these hooks.
  await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  if (!mounted || !taskListRef.value?.isConnected || !isAuthReady.value || !isAuthenticated.value || listOwner !== taskSessionKey() || !router.currentRoute.value.path.replace(/\/$/, '').endsWith('/clips/tasks')) return;
  const card = Array.from(taskListRef.value.querySelectorAll<HTMLElement>('[data-package-id]')).find(item => item.dataset.packageId === lastOpenedPackageId.value);
  card?.scrollIntoView({ block: 'center', behavior: 'auto' });
  card?.focus({ preventScroll: true });
};
const removeTaskLoadingHook = nuxtApp.hook('page:loading:end', restoreClipTaskPosition);
const removeTaskTransitionHook = nuxtApp.hook('page:transition:finish', restoreClipTaskPosition);
onUnmounted(() => { releasePaginationFocus(); mounted = false; ++listRequest; ++highlightRequest; removeTaskLoadingHook(); removeTaskTransitionHook(); });
</script>
